import { useCallback, useEffect, useRef } from 'react';
import { View } from 'react-native';
import { BaseModal } from '../ui/BaseModal';
import { useMenuContext } from '../../src/layout/contexts/MenuContext';
import { LayeredAvatarLab } from '../avatars/LayeredAvatarLab/LayeredAvatarLab';
import { globalLayeredAvatarModalStyles as styles } from './GlobalLayeredAvatarModal.styles';

const FULL_SCREEN_POSITION = { top: 0, right: 0, bottom: 0, left: 0 };
const MENU_REOPEN_DELAY_MS = 280;

export function GlobalLayeredAvatarModal() {
  const {
    layeredAvatarLabVisible,
    reopenMenuOnCustomizerClose,
    closeLayeredAvatarLab,
    clearReopenMenuOnCustomizerClose,
    setMenuVisible,
  } = useMenuContext();
  const reopenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasVisibleRef = useRef(false);

  // Only a customizer drilled into from the menu drawer returns to it. Opened from the
  // Profile screen (or anywhere else), closing must leave the user where they were.
  useEffect(() => {
    if (layeredAvatarLabVisible) {
      wasVisibleRef.current = true;
      if (reopenTimeoutRef.current) {
        clearTimeout(reopenTimeoutRef.current);
        reopenTimeoutRef.current = null;
      }
      return;
    }

    if (!wasVisibleRef.current) return;

    wasVisibleRef.current = false;
    if (!reopenMenuOnCustomizerClose) return;

    // Consume the flag so a later open from elsewhere can never inherit this reopen.
    clearReopenMenuOnCustomizerClose();
    reopenTimeoutRef.current = setTimeout(() => {
      setMenuVisible(true);
      reopenTimeoutRef.current = null;
    }, MENU_REOPEN_DELAY_MS);
  }, [
    layeredAvatarLabVisible,
    reopenMenuOnCustomizerClose,
    clearReopenMenuOnCustomizerClose,
    setMenuVisible,
  ]);

  useEffect(
    () => () => {
      if (reopenTimeoutRef.current) {
        clearTimeout(reopenTimeoutRef.current);
      }
    },
    []
  );

  const handleClose = useCallback(() => {
    closeLayeredAvatarLab();
  }, [closeLayeredAvatarLab]);

  return (
    <BaseModal
      isVisible={layeredAvatarLabVisible}
      onDismiss={handleClose}
      contentAlignment="flex-start"
      contentPosition={FULL_SCREEN_POSITION}
      contentContainerStyle={styles.content}
      testID="global-layered-avatar-modal"
    >
      <View style={styles.inner}>
        <LayeredAvatarLab onClose={handleClose} />
      </View>
    </BaseModal>
  );
}
